
import { useRef, useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Play } from "lucide-react";

export default function VideoTrailer() {
  const videoRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBioOpen, setIsBioOpen] = useState(true); // Changed to true by default
  const [hasObserved, setHasObserved] = useState(false);

  useEffect(() => {
    // Only set up observer if IntersectionObserver exists and we haven't already observed
    if (typeof IntersectionObserver !== 'undefined' && !hasObserved) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            setHasObserved(true);
            observer.disconnect();
          }
        },
        {
          root: null,
          threshold: 0.2,
        }
      );

      if (videoRef.current) {
        observer.observe(videoRef.current);
      }

      return () => observer.disconnect();
    } else if (!hasObserved) {
      // Fallback for browsers without IntersectionObserver
      setIsVisible(true);
      setHasObserved(true);
    }
  }, [hasObserved]);

  const handlePlayClick = () => {
    setIsPlaying(true);
  };

  const toggleBio = () => {
    setIsBioOpen(!isBioOpen);
  };

  const bioImageUrl = "/lovable-uploads/20b29789-0c27-4ca5-bc2c-8738168184c4.png";
  const thumbnailUrl = "/lovable-uploads/c40cb290-6bba-4300-a3b7-3dfa5c6877c5.png";
  const signatureUrl = "/lovable-uploads/ec4efc9e-2e09-4e48-8f93-d2d73d94e210.png";

  return (
    <section 
      ref={videoRef} 
      className="py-16 bg-white border-t border-charcoal/10"
      aria-labelledby="meet-your-guide"
    >
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <div className={`opacity-0 ${isVisible ? "animate-fade-in" : ""}`}>
            <h3 id="meet-your-guide" className="text-2xl md:text-3xl font-serif mb-8 text-center">Meet Your Guide</h3>
            
            <div className="relative aspect-video bg-[#2B2A2D] overflow-hidden rounded-sm shadow-xl border border-cream/5 transition-all duration-500 hover:shadow-2xl hover:border-cream/20 group">
              {!isPlaying ? (
                <div className="absolute inset-0 flex items-center justify-center cursor-pointer" onClick={handlePlayClick}>
                  {/* Premium play button overlay */}
                  <div className="relative z-20 flex items-center justify-center w-24 h-24 bg-white/10 backdrop-blur-sm rounded-full transition-all duration-500 group-hover:bg-white/20 group-hover:scale-105 border border-white/30 shadow-[0_0_25px_rgba(255,255,255,0.15)]">
                    <Play size={40} className="text-white fill-white/5" />
                  </div>
                  
                  {/* Refined gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 to-transparent z-10"></div>
                  
                  {/* Centered image with premium styling */}
                  <div className="absolute inset-0 flex items-center justify-center z-0">
                    <img 
                      src={thumbnailUrl}
                      alt="Video thumbnail showing Tommy Zee of the Monastery Method" 
                      className="w-[75%] h-auto object-contain transform transition-transform duration-700 group-hover:scale-[1.02]"
                      width="1280"
                      height="720"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
              ) : (
                <iframe 
                  src="https://player.vimeo.com/video/1051584044?autoplay=1" 
                  title="The Monastery Method Trailer"
                  allow="autoplay; fullscreen; picture-in-picture" 
                  className="absolute top-0 left-0 w-full h-full"
                  frameBorder="0"
                  loading="lazy"
                ></iframe>
              )}
            </div>
            
            <div className="mt-8">
              <button 
                onClick={toggleBio} 
                className="flex items-center justify-center mx-auto text-charcoal/80 hover:text-charcoal transition-colors duration-300 group"
                aria-expanded={isBioOpen}
                aria-controls="bio-content"
              >
                <span className="text-sm uppercase tracking-wider mr-1 font-light">About Your Guide</span>
                <div className="w-6 h-6 flex items-center justify-center rounded-full border border-charcoal/20 group-hover:border-charcoal/40 transition-all duration-300">
                  {isBioOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </div>
              </button>
              
              <div 
                id="bio-content"
                className={`overflow-hidden transition-all duration-500 ease-in-out mt-4 max-w-3xl mx-auto ${
                  isBioOpen ? "max-h-[1200px] opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="py-10 px-6 md:px-10 bg-offwhite rounded-sm shadow-sm mt-2 border-t border-charcoal/5">
                  <div className="flex flex-col md:flex-row items-center gap-10">
                    <div className="md:w-1/3 flex-shrink-0">
                      <div className="aspect-square w-full overflow-hidden rounded-full border border-[#FEF7CD]/20 shadow-md relative -ml-2 mt-1">
                        <img 
                          src={bioImageUrl}
                          alt="Tommy Zee portrait" 
                          className="w-full h-full object-cover"
                          width="400"
                          height="400"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    </div>
                    <div className="md:w-2/3">
                      <h4 className="text-2xl font-serif mb-4 text-charcoal font-light tracking-wide">Tommy Zee</h4>
                      <div className="space-y-4 text-softgray/90 leading-relaxed">
                        <p>From transforming global brands like Nike, Google, and Adidas to guiding change at TD Bank, my journey spans three continents and over a thousand projects. Yet the most profound discovery came from an unexpected source.</p>
                        <p>While exploring Eastern practices seeking balance, I found the answer in our Western heritage – the Catholic monastic tradition that perfected the integration of contemplation and action.</p>
                        <p>Today, I guide successful leaders to the intersection of achievement and peace. Drawing from timeless wisdom refined over centuries, I help transform what you've already built into something even more extraordinary: success that fulfills rather than depletes.</p>
                        <div className="py-2">
                          <p className="font-serif text-charcoal/80 italic">The glory of God is man fully alive.</p>
                          <p>This is the essence of my work.</p>
                        </div>
                        <div className="mt-4 pt-2 border-t border-charcoal/10">
                          <img 
                            src={signatureUrl} 
                            alt="Tommy Zee signature" 
                            className="h-16 w-auto mx-0 opacity-90"
                            width="180"
                            height="64"
                            loading="lazy"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
